import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { playSound, Sound } from '~/assets/sounds';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction } from '~/shared';
import { ChatCategory } from '../ChatCategory';
import { ChatInput } from '../ChatInput';
import { PlayerMessage, ServerMessage } from '../ChatMessages';
import {
	StyledCategoriesContainer,
	StyledChatContainer,
	StyledContainer,
	StyledTagContainer,
} from './Chat.styled';

const MESSAGE_HIGHLIGHT_TIMEOUT_MS = 30000;

const Chat: React.FC = () => {
	const channels = useObservable(rage.chat.getChannels());
	const activeChannel = useObservable(rage.chat.getActiveChannelId());
	const messages = useObservable(rage.chat.getMessages());
	const [inputVisible, setInputVisible] = useState<boolean>(false);
	const [highlightedMessages, setHighlightedMessages] = useState<Set<number>>(
		new Set(),
	);
	const scrollRef = useRef(null);
	const [scrollTrigger, setScrollTrigger] = useState(0);
	const previousMessagesLength = useRef(0);
	const previousInputVisible = useRef(inputVisible);
	const hideTimersRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

	const clearHideTimers = () => {
		hideTimersRef.current.forEach((timer) => clearTimeout(timer));
		hideTimersRef.current.clear();
	};

	const highlightAllMessagesWithTimeout = () => {
		clearHideTimers();

		const allMessageIndices = messages.map((_, index) => index);
		setHighlightedMessages(new Set(allMessageIndices));

		allMessageIndices.forEach((index) => {
			const timer = setTimeout(() => {
				setHighlightedMessages((prev) => {
					const newSet = new Set(prev);
					newSet.delete(index);
					return newSet;
				});
				hideTimersRef.current.delete(index);
			}, MESSAGE_HIGHLIGHT_TIMEOUT_MS);

			hideTimersRef.current.set(index, timer);
		});
	};

	useLayoutEffect(() => {
		if (scrollRef.current && !inputVisible) {
			const container = scrollRef.current as any;
			container.scrollTop = container.scrollHeight;
		}
	}, [messages, inputVisible, scrollTrigger]);

	useEffect(() => {
		rage.chat
			.setCursorVisible(inputVisible)
			.catch((err) => rage.notifications.showException(err));
	}, [inputVisible]);

	useEffect(() => {
		return () => {
			clearHideTimers();
		};
	}, []);

	useEffect(() => {
		if (!previousInputVisible.current && inputVisible) {
			clearHideTimers();
		} else if (previousInputVisible.current && !inputVisible) {
			highlightAllMessagesWithTimeout();
		}

		previousInputVisible.current = inputVisible;
	}, [inputVisible]);

	useEffect(() => {
		if (inputVisible) return;

		highlightAllMessagesWithTimeout();
		previousMessagesLength.current = messages.length;
	}, [activeChannel]);

	useEffect(() => {
		if (inputVisible) {
			previousMessagesLength.current = messages.length;
			return;
		}

		if (messages.length < previousMessagesLength.current) {
			highlightAllMessagesWithTimeout();
			previousMessagesLength.current = messages.length;
			return;
		}

		if (messages.length > previousMessagesLength.current) {
			const newIndices: number[] = [];
			for (let i = previousMessagesLength.current; i < messages.length; i++) {
				newIndices.push(i);
			}

			setHighlightedMessages((prev) => {
				const newSet = new Set(prev);
				newIndices.forEach((i) => newSet.add(i));
				return newSet;
			});

			newIndices.forEach((index) => {
				if (hideTimersRef.current.has(index)) return;

				const timer = setTimeout(() => {
					setHighlightedMessages((prev) => {
						const newSet = new Set(prev);
						newSet.delete(index);
						return newSet;
					});
					hideTimersRef.current.delete(index);
				}, MESSAGE_HIGHLIGHT_TIMEOUT_MS);
				hideTimersRef.current.set(index, timer);
			});
		}

		previousMessagesLength.current = messages.length;
	}, [messages.length, inputVisible]);

	useInputAction(InputAction.HudChatShow, () => {
		setInputVisible(true);
	});

	const changeActiveChannel = (id: string) => {
		rage.chat.setActiveChannelId(id);
	};

	const getMessageOpacity = (messageIndex: number): number => {
		if (inputVisible) return 1;

		return highlightedMessages.has(messageIndex) ? 1 : 0.25;
	};

	return (
		<StyledContainer>
			<StyledCategoriesContainer>
				{channels.map((channel) => (
					<ChatCategory
						name={channel.id}
						title={channel.title}
						key={channel.id}
						active={activeChannel === channel.id}
						hasUnreadMessages={channel.hasUnreadMessages}
						changeActiveChannelHandler={changeActiveChannel}
					/>
				))}
			</StyledCategoriesContainer>
			<StyledChatContainer ref={scrollRef} tabIndex={-1}>
				{messages.map((message, index) => {
					const messageOpacity = getMessageOpacity(index);

					if (message.sender) {
						return (
							<div
								key={index}
								style={{
									opacity: messageOpacity,
									transition: 'opacity 0.3s ease',
								}}
							>
								<PlayerMessage
									username={message.sender}
									text={message.text}
									usernameColor={message.color}
									onRender={() => setScrollTrigger((prev) => prev + 1)}
								/>
							</div>
						);
					}
					return (
						<div
							key={index}
							style={{
								opacity: messageOpacity,
								transition: 'opacity 0.3s ease',
							}}
						>
							<StyledTagContainer>Сервер</StyledTagContainer>
							<ServerMessage
								text={message.text}
								color={message.color}
								onRender={() => setScrollTrigger((prev) => prev + 1)}
							/>
						</div>
					);
				})}
			</StyledChatContainer>
			<CSSTransition
				in={inputVisible}
				classNames="fade"
				timeout={250}
				unmountOnExit
			>
				<ChatInput
					onMessageSend={() => setInputVisible(false)}
					onMouseEnter={() => {
						if (inputVisible) {
							playSound(Sound.LinkCursor);
						}
					}}
				/>
			</CSSTransition>
		</StyledContainer>
	);
};

export { Chat };
